import React, { useState, useEffect, useRef } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsForm from '../core/HeadersAndFooters/FooterButtonsForm';
import ConfirmationModal from '../core/ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ImageCropper from '../core/ImageCropper/ImageCropper';
import { Upload } from 'lucide-react';
import FormattingToolbar from '../shared/FormattingToolbar';
import { applyTextFormatting } from '../../utils/formatText';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useTranslation } from '../../i18n/useTranslation';

// Import protected Supabase client and direct fetch helper from shared location
import { supabaseFetch } from '../../lib/supabase';
import { eventService, groupService } from '../../data/dataService';
import { getBoundaryNamesInOrder, getAreaParishes } from '../../data/directoryService';
import ParishSelect from '../shared/ParishSelect';
import { baseParishId } from '../../utils/scopeResolution';
import { getIsPlatformContent } from '../../data/articleMutationService';
import { tokens } from '../../styles/designTokens';
import { addBreadcrumb, addCaughtError } from '../../utils/errorCapture';
import {
  ARTICLE_TITLE_MAX, ARTICLE_SUMMARY_MAX, ARTICLE_CONTENT_MAX, ARTICLE_IMAGE_CAPTION_MAX
} from '../../utils/validationConstants';
import { parseEventDate } from '../../utils/eventDateTime';
import { pickTagImage, isTagImageUrl } from '../../utils/tagImageMatcher';
import { isRateLimited, rateLimitMessage, RATE_LIMIT_TITLE_KEY } from '../../utils/rateLimitMessage';
import { isGroupClosingError, GROUP_CLOSING_MESSAGE_KEY } from '../../utils/groupClosingError';

const AddEditEvent = ({ userId, onBack, currentContext, mode = 'add', event = null, preselectedGroupId = null, selectedArea = null }) => {
  const { t } = useTranslation('events');
  const { t: tTags } = useTranslation('tags');
  // Locality mode: publishing directly to the user's locality with no group required.
  // Active when opened from locality context without a preselected group.
  const isLocalityMode = !preselectedGroupId && !event?.community_id &&
    (currentContext === 'locality' || currentContext?.id === 'locality' || currentContext?.type === 'location');
  const { isMobile } = useMediaQuery();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  // #78 outcome 1: true once the group list has loaded.
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    cover_image_url: '',
    image_caption: '',
    community_id: '',
    is_public: true
  });

  const [eventDetails, setEventDetails] = useState({
    start_date: '',
    end_date: '',
    display_from: '',
    is_all_day: false,
    timezone: 'UTC',
    venue_name: '',
    venue_address: '',
    venue_postcode: '',
    is_online: false,
    max_attendees: '',
    price_info: '',
    contact_email: '',
    contact_phone: '',
    requires_rsvp: false,
    rsvp_deadline: ''
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });

  // Tag-picker state (closed vocabulary, replaces the old free-text article_tags
  // control — brief docs/briefs/2026-08-11-tag-picker-addeditevent-build.md §2).
  // tagVocabulary: [{ id, display_order, i18n_key, terms: [{ id, category_id, display_order, i18n_key }] }]
  const [tagVocabulary, setTagVocabulary] = useState([]);
  const [tagVocabularyLoading, setTagVocabularyLoading] = useState(true);
  const [tagSlugs, setTagSlugs] = useState([]);

  // R4 house fallback URL (image-layer build) — fetched once, used on save so
  // a tag-default cover image can fall back to the house image when no tag
  // has a matched illustration yet.
  const [houseFallbackUrl, setHouseFallbackUrl] = useState(null);

  // Hero image state (image system)
  const [heroImageDisplay, setHeroImageDisplay] = useState('');
  const [heroImageThumbnail, setHeroImageThumbnail] = useState('');
  const [heroImageAspect, setHeroImageAspect] = useState('4:3');
  const [showHeroCropper, setShowHeroCropper] = useState(false);

  // Tracks article ID created during 'add' mode so retries update rather than duplicate
  const [savedArticleId, setSavedArticleId] = useState(null);

  // R2 (docs/briefs/2026-08-15-content-keeps-published-privacy.md): the community_id — and the
  // is_public it was stored under — an edit-mode form was hydrated with. The derivation effect
  // below uses the pair to tell "this is the group the event was already filed under" from "the
  // author just changed it", and to actively RESTORE is_public (not just leave it alone) if the
  // author selects a different group and then selects back to the original one within the same
  // edit session — a plain skip-on-match guard would otherwise leave is_public at whatever the
  // detour derived, since community_id nets out unchanged but is_public was already mutated.
  // Both null in add mode, so the "unchanged" branch never suppresses derivation there.
  const initialCommunityIdRef = useRef(null);
  const initialIsPublicRef = useRef(null);

  // Step 7B: home_locality_id tagging state
  const [homeLocalityId, setHomeLocalityId] = useState(null);
  const [homeLocalityBoundaries, setHomeLocalityBoundaries] = useState([]); // [{id, name}] for picker
  const [homeLocalitySource, setHomeLocalitySource] = useState(null); // 'group'|'group-unlocated'|'group-error'|'area-choice'|'no-home'|'existing'
  const [homeLocalityLoading, setHomeLocalityLoading] = useState(true);
  // Group content (#72): the resolver's status for the selected group ('located'|'unlocated'), and
  // the name of the locality the event will appear in, for the read-only line.
  const [groupLocalityStatus, setGroupLocalityStatus] = useState(null);
  const [homeLocalityName, setHomeLocalityName] = useState(null);
  // #78: only a locality the member picks counts as a change; the resolver's default does not.
  const [pickedLocalityId, setPickedLocalityId] = useState(null);
  // #78: true once edit mode has put the saved event (and its details) into the form.
  const [editLoaded, setEditLoaded] = useState(false);

  // Logo click listener - navigate back to MainFeed
  // Asks first when there are unsaved changes (#78); confirmLeaveRef is set once the hook below runs.
  const confirmLeaveRef = useRef(null);
  const contentTextareaRef = useRef(null);
  useEffect(() => {
    const handleLogoClick = async () => {
      const leave = confirmLeaveRef.current ? await confirmLeaveRef.current() : true;
      if (leave && onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Calculate dynamic header title including selected group/locality name
  const getHeaderTitle = () => {
    const baseTitle = mode === 'edit' ? t('edit_event') : t('new_event');

    if (formData.community_id) {
      const selectedCommunity = groups.find(g => g.id === formData.community_id);
      if (selectedCommunity) {
        return `${baseTitle} (${selectedCommunity.name})`;
      }
    }

    return baseTitle;
  };

  const headerTitle = getHeaderTitle();

  // Read-only status text for the event
  const statusText = (() => {
    if (mode !== 'edit') return t('status_draft');
    // In edit mode, use the passed event.is_published flag when available
    if (event && (event.is_published === true || event.is_published === false)) {
      return event.is_published ? t('status_published') : t('status_draft');
    }
    // Fallback to Draft
    return t('status_draft');
  })();

  // Load the ratified tag vocabulary once on mount — same inline-fetch idiom the
  // rest of this component already uses for groups/boundaries (state report
  // .claude/state-reports/2026-08-11-addeditevent-tag-editing-state-probe.md #4),
  // with the actual query in eventService.getTagVocabulary() per FILE_STRUCTURE.md
  // § Data Layer (data fetching belongs in /src/data/, not the component).
  useEffect(() => {
    let cancelled = false;
    eventService.getTagVocabulary().then(vocab => {
      if (cancelled) return;
      setTagVocabulary(vocab);
      setTagVocabularyLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // R4 house fallback URL, once on mount (image-layer build).
  useEffect(() => {
    let cancelled = false;
    eventService.getTagHouseFallbackImageUrl().then(url => {
      if (!cancelled) setHouseFallbackUrl(url);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Do nothing until a userId is provided
    if (!userId) return;

    const loadUserData = async () => {
      try {
        // Load user's group memberships first
        const { data: memberships, error: membershipsError } = await supabaseFetch.query(
          'community_members',
          { select: 'community_id', eq: { user_id: userId } }
        );

        if (membershipsError) {
          console.warn('Error loading user memberships for', userId, membershipsError);
          return;
        }

        if (!memberships || memberships.length === 0) {
          setGroups([]);
          setGroupsLoaded(true);
          return;
        }

        // Get community IDs and fetch community details
        const communityIds = memberships.map(m => m.community_id);
        const { data: communities, error: communitiesError } = await supabaseFetch.query(
          'communities',
          { select: 'id, name, deleted_at', inFilter: { id: communityIds } }
        );

        if (communitiesError) {
          console.warn('Error loading communities:', communitiesError);
          return;
        }

        // A soft-deleted group is not a real choice (#78 outcome 1).
        const groupList = (communities || []).filter(c => !c.deleted_at).map(c => ({
          id: c.id,
          name: c.name
        }));
        setGroups(groupList);
        setGroupsLoaded(true);
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, [userId]);

  // If opened in edit mode, populate form with the passed event
  useEffect(() => {
    if (mode !== 'edit' || !event) return;

    const loadEventData = async () => {
      // Map event fields into formData safely
      const mapped = {
        title: event.title || '',
        summary: event.summary || '',
        content: event.content || '',
        cover_image_url: event.cover_image_url || '',
        image_caption: event.image_caption || '',
        community_id: event.community_id || '',
        is_public: event.is_public !== undefined ? event.is_public : true
      };

      initialCommunityIdRef.current = mapped.community_id;
      initialIsPublicRef.current = mapped.is_public;
      setFormData(prev => ({ ...prev, ...mapped }));
      setTagSlugs(Array.isArray(event.tag_slugs) ? event.tag_slugs : []);

      // Fetch event_details from database
      try {
        const { data: eventDetailsData, error: eventDetailsError } = await supabaseFetch.query(
          'event_details',
          { select: '*', eq: { article_id: event.id }, single: true }
        );

        if (eventDetailsError) {
          console.warn('No event details found for article:', event.id, eventDetailsError);
        } else if (eventDetailsData) {
          // Map event_details fields from database
          const eventDetailsMapped = {
            start_date: eventDetailsData.start_date || '',
            end_date: eventDetailsData.end_date || '',
            display_from: eventDetailsData.display_from || '',
            is_all_day: eventDetailsData.is_all_day || false,
            timezone: eventDetailsData.timezone || 'UTC',
            venue_name: eventDetailsData.venue_name || '',
            venue_address: eventDetailsData.venue_address || '',
            venue_postcode: eventDetailsData.venue_postcode || '',
            is_online: eventDetailsData.is_online || false,
            max_attendees: eventDetailsData.max_attendees || '',
            price_info: eventDetailsData.price_info || '',
            contact_email: eventDetailsData.contact_email || '',
            contact_phone: eventDetailsData.contact_phone || '',
            requires_rsvp: eventDetailsData.requires_rsvp || false,
            rsvp_deadline: eventDetailsData.rsvp_deadline || ''
          };

          setEventDetails(prev => ({ ...prev, ...eventDetailsMapped }));
        }
      } catch (error) {
        console.error('Error loading event details:', error);
      }

      if (mapped.cover_image_url) {
        setImagePreview(mapped.cover_image_url);
        setHeroImageDisplay(mapped.cover_image_url);
      }
      setEditLoaded(true);
    };

    loadEventData();
  }, [mode, event]);

  // If preselectedGroupId is provided (from "Create New Event" button), set community_id
  useEffect(() => {
    if (preselectedGroupId && mode === 'add') {
      console.log('Setting preselected group:', preselectedGroupId);
      setFormData(prev => ({ ...prev, community_id: preselectedGroupId }));
    }
  }, [preselectedGroupId, mode]);

  // R1 (docs/briefs/2026-08-15-fix-event-is-public-default.md) + R2
  // (docs/briefs/2026-08-15-content-keeps-published-privacy.md): is_public derives from the
  // selected group's privacy for NEW content and for a deliberate group change the author makes
  // in the form — never for existing content left exactly as the author found it. R2 narrows
  // addendum-1 §D's original "always override, including in edit mode" ruling, which is now
  // superseded: the corrected DB trigger (migration 20260815 + the content-keeps-published-
  // privacy correction) itself only recomputes on INSERT or a real community_id change, so the
  // form must match that or it goes back to displaying one thing while the database stores
  // another — the exact defect this whole saga started from.
  //
  // Query lives in eventService.getCommunityPrivacy, not inline here, per FILE_STRUCTURE.md
  // § Data Layer — the same rule this file's tag-vocabulary fetch above already follows.
  useEffect(() => {
    // Unchanged from what this event was already filed under (grouped or ungrouped) — RESTORE
    // what is stored, exactly as the DB trigger would leave it on an ordinary edit. Actively
    // resets rather than just skipping: if the author selected a different group and then
    // selected back to this one within the same edit session, is_public was already mutated by
    // that detour and community_id netting out unchanged does not undo it on its own — the
    // deployed trigger only recomputes on a real OLD/NEW community_id difference, so on save this
    // would otherwise silently carry the detour's derived value through as an "ordinary edit".
    if (mode === 'edit' && formData.community_id === initialCommunityIdRef.current) {
      setFormData(prev => (
        prev.is_public === initialIsPublicRef.current
          ? prev
          : { ...prev, is_public: initialIsPublicRef.current }
      ));
      return;
    }

    if (!formData.community_id) {
      // A deliberate move OUT of a group, mid-edit: the DB trigger does not derive for a NULL
      // community_id either (content-keeps-published-privacy.md addendum-1 §A, fail-closed) — it
      // leaves is_public exactly as it was under the old group. The form must not force it to
      // true here, or it would show a value the database will not actually store.
      if (mode === 'edit') return;
      // New content with no group selected (or none yet): true is the sensible default, and
      // there is no stored value here to disturb.
      setFormData(prev => (prev.is_public === true ? prev : { ...prev, is_public: true }));
      return;
    }

    let cancelled = false;
    eventService.getCommunityPrivacy(formData.community_id).then(isPrivate => {
      if (cancelled || isPrivate === null) return;
      setFormData(prev => ({ ...prev, is_public: !isPrivate }));
    });
    return () => { cancelled = true; };
  }, [formData.community_id, mode]);

  // Step 7B: Resolve home_locality_id (precedence chain).
  // Content saved into a group follows the group (#72, ruled by Michael 27 Sep 2026 —
  // docs/briefs/2026-09-27-unlocated-group-events-stay-in-group.md, addenda 1-3), whatever context
  // the form was opened from: the group's locality, never the author's own or the one being
  // browsed. A public group with several boundaries offers a picker limited to them (interim,
  // until #73); a group with no location gives no locality (group only, author told); a failed
  // lookup blocks saving. Keyed on formData.community_id — the value that is saved. (In locality
  // mode the event is saved with no group, so it follows the locality rules.)
  // Edit keeps the stored home_locality_id unless the author changes the group or picks another
  // of its boundaries.
  useEffect(() => {
    // A group chosen in locality mode makes it group content too (#72 follow-ups Part 2).
    const groupId = formData.community_id || null;

    if (mode === 'edit' && !groupId) {
      if (event?.home_locality_id) {
        setHomeLocalityId(event.home_locality_id);
        setHomeLocalitySource('existing');
      }
      setGroupLocalityStatus(null);
      setHomeLocalityLoading(false);
      return;
    }

    let cancelled = false;

    const resolveHomeLocality = async () => {
      setHomeLocalityLoading(true);

      // Precedence 1: group content follows the group.
      if (groupId) {
        const res = await groupService.resolveGroupHomeLocality(groupId);
        if (cancelled) return;

        if (mode === 'edit' && groupId === initialCommunityIdRef.current) {
          // Unchanged group in edit: keep what is stored; the picker (multi-boundary groups)
          // lets the author move it to another of the group's boundaries.
          const stored = event?.home_locality_id ? Number(event.home_locality_id) : null;
          let options = res.status === 'located' ? res.boundaries : [];
          let storedName = options.find(b => b.id === stored)?.name
            || (stored && stored === res.homeLocalityId ? res.boundaryName : null);
          if (stored && !storedName) {
            const { data } = await getBoundaryNamesInOrder([stored]);
            if (cancelled) return;
            storedName = data[0]?.name || null;
            if (options.length > 1) options = [{ id: stored, name: storedName || String(stored) }, ...options];
          }
          setHomeLocalityId(stored);
          setHomeLocalityName(storedName);
          setHomeLocalityBoundaries(options);
          setGroupLocalityStatus(res.status === 'error' ? null : res.status);
          setHomeLocalitySource('existing');
          setHomeLocalityLoading(false);
          return;
        }

        setHomeLocalityId(res.homeLocalityId);
        setHomeLocalityName(res.boundaryName);
        setHomeLocalityBoundaries(res.boundaries);
        setGroupLocalityStatus(res.status);
        setHomeLocalitySource(res.status === 'located' ? 'group'
          : res.status === 'unlocated' ? 'group-unlocated' : 'group-error');
        setHomeLocalityLoading(false);
        return;
      }

      setGroupLocalityStatus(null);
      setHomeLocalityName(null);

      // Which area a locality post is placed from (own-parish addenda 1–2): the area being browsed
      // (a saved area of the member's own), else their home area. The freshly read home row is
      // used when the browsed area IS home. A temporary visit is not an area and changes nothing
      // here (the post goes to the browsed saved area's parishes, as before).
      let homeArea = null;
      try {
        const { data, error } = await supabaseFetch.query('user_areas', {
          select: 'id,boundary_ids,primary_boundary_id',
          eq: { user_id: userId, is_home: true },
          single: true
        });
        if (!error) homeArea = data || null;
      } catch (err) {
        console.warn('Step 7B: Home area lookup failed:', err);
      }
      if (cancelled) return;

      const browsed = currentContext === 'locality' && selectedArea?.boundary_ids?.length > 0 ? selectedArea : null;
      const area = browsed && (!homeArea || browsed.id !== homeArea.id) ? browsed : homeArea;

      // "Publishing to" chooses among that area's parishes, in their saved order, defaulting to its
      // own (base) parish. The choice is the post's home_locality_id — where it is stored and
      // appears — and its map point follows.
      if (baseParishId(area) != null) {
        const { options, defaultId, error: namesError } = await getAreaParishes(area);
        if (cancelled) return;
        if (namesError) console.warn('Publishing to: parish names could not be read:', namesError);
        setHomeLocalityBoundaries(options);
        setHomeLocalityId(defaultId);
        setHomeLocalityName(options.find(b => b.id === defaultId)?.name || null);
        setHomeLocalitySource('area-choice');
        setHomeLocalityLoading(false);
        return;
      }

      if (cancelled) return;
      // No home area — soft block
      setHomeLocalityId(null);
      setHomeLocalityBoundaries([]);
      setHomeLocalitySource('no-home');
      setHomeLocalityLoading(false);
    };

    resolveHomeLocality();
    return () => { cancelled = true; };
  }, [mode, isLocalityMode, currentContext, formData.community_id, selectedArea, userId, event]);

  // #72: content saved into a group, and whether that group is private (its content appears in no
  // locality feed, so no locality line or picker is shown). is_public derives from the group.
  const isGroupContent = !!formData.community_id;

  // #78 outcome 1: the event is saved in a group this form cannot offer (deleted, left, or not a
  // member). Say so plainly, keep that group as the value, and offer no false choice.
  const savedCommunityId = mode === 'edit' ? (event?.community_id || null) : null;

  // Addendum 4: a platform-wide post keeps its flag and placement exactly as stored; only Content
  // Moderation changes them (migration 20260928f's guard refuses the form otherwise).
  const [isPlatformContent, setIsPlatformContent] = useState(mode === 'edit' && event?.is_platform_content === true);
  useEffect(() => {
    if (mode !== 'edit' || !event?.id) return;
    let cancelled = false;
    getIsPlatformContent(event.id).then(flag => {
      if (!cancelled && flag !== null) setIsPlatformContent(flag);
    });
    return () => { cancelled = true; };
  }, [mode, event?.id]);
  // An edit changes only what the editor changed (addendum 5 ruling 2): the author is never
  // re-written, and a platform post's placement is left as stored (addendum 4), by not sending them.
  // The flag is read fresh at save so a slow first read cannot decide it.
  const keepStoredFields = async (articleData, articleId) => {
    if (!articleId) return;
    delete articleData.author_id;
    const flag = await getIsPlatformContent(articleId);
    if (flag ?? (articleId === event?.id && isPlatformContent)) {
      delete articleData.home_locality_id;
      delete articleData.location_point;
    }
  };
  const savedGroupUnavailable = !!savedCommunityId && groupsLoaded
    && formData.community_id === savedCommunityId
    && !groups.some(g => g.id === savedCommunityId);
  const isPrivateGroupContent = isGroupContent && formData.is_public === false;

  const handleLocalityPick = (value) => {
    const id = value ? Number(value) : null;
    setHomeLocalityId(id);
    setHomeLocalityName(homeLocalityBoundaries.find(b => b.id === id)?.name || null);
    setPickedLocalityId(id);
  };

  // Where leaving goes: a form opened in a window (My Articles, Pending Events) closes it;
  // otherwise My Articles.
  const leaveForm = () => {
    if (mode === 'edit' && onBack) {
      onBack();
    } else {
      window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'articles', scrollToTop: true } }));
    }
  };

  // #78 items 4–5: "unsaved changes" means changed since the form loaded. is_public is left out:
  // it follows the group and the form sets it itself. Only a locality the member picks counts.
  const { is_public: _derivedIsPublic, ...editableFields } = formData;
  const { confirmLeave, markSaved, dialog: leaveDialog } = useUnsavedChanges({
    value: {
      editableFields, eventDetails, tagSlugs, heroImageDisplay, heroImageThumbnail,
      heroImageAspect, pickedLocalityId
    },
    ready: mode === 'edit'
      ? editLoaded
      : (!preselectedGroupId || formData.community_id === preselectedGroupId),
    onLeave: leaveForm,
  });
  confirmLeaveRef.current = confirmLeave;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Formatting toolbar action on the description: the same text edit as the article editor.
  const applyContentFormatting = (type) => {
    const textarea = contentTextareaRef.current;
    if (!textarea) return;
    const result = applyTextFormatting(formData.content, textarea.selectionStart, textarea.selectionEnd, type, {
      bold: t('articles:bold_text'),
      italic: t('articles:italic_text')
    });
    if (!result) return;
    handleInputChange('content', result.text);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(result.cursor, result.cursor);
    }, 0);
  };

  const handleEventDetailChange = (field, value) => {
    setEventDetails(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Closed-vocabulary toggle — ticking a term adds its slug, ticking it again
  // (from the checklist or its chip) removes it. No free entry (brief §3C).
  const handleToggleTagSlug = (slug) => {
    setTagSlugs(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
  };

  // Tag-image matcher, folded into save (image-layer build §4/§9) rather than a
  // separate follow-up write — this component's save is already one atomic
  // insert/update carrying both cover_image_url and tag_slugs (unlike the
  // importer, which must patch after import_event() returns an id). R5
  // override precedence: only replace `coverImageUrl` when it's empty, or
  // already one of the tag illustrations (so changing tags updates a
  // tag-default picture live) — never when it holds anything else (a
  // steward's own uploaded/cropped photo, left untouched).
  const resolveCoverImageUrl = (coverImageUrl) => {
    if (coverImageUrl && !isTagImageUrl(coverImageUrl)) return coverImageUrl;
    return pickTagImage(tagSlugs, tagVocabulary) || houseFallbackUrl || coverImageUrl;
  };

  // Hero image save handler (image system)
  const handleHeroImageSave = ({ displayUrl, thumbnailUrl, aspectRatio }) => {
    setHeroImageDisplay(displayUrl);
    setHeroImageThumbnail(thumbnailUrl);
    setHeroImageAspect(aspectRatio);
    // Also update the existing cover_image_url for backward compatibility
    setImagePreview(displayUrl);
    setFormData(prev => ({ ...prev, cover_image_url: displayUrl }));
    setErrors(prev => ({ ...prev, image: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t('error_title_required');
    } else if (formData.title.length > 180) {
      newErrors.title = t('error_title_too_long');
    }

    if (!formData.summary.trim()) {
      newErrors.summary = t('error_introduction_required');
    } else if (formData.summary.length > 300) {
      newErrors.summary = t('error_introduction_too_long');
    }

    if (!formData.content.trim()) {
      newErrors.content = t('error_content_required');
    } else if (formData.content.length > ARTICLE_CONTENT_MAX) {
      // The shared limit (the database's chk_articles_content_length, and the counter below).
      newErrors.content = t('error_content_too_long', { max: ARTICLE_CONTENT_MAX });
    }

    if (!isLocalityMode && !formData.community_id) {
      newErrors.community_id = t('error_select_group');
    }

    // Anchorability backstop: the locality feed RPC gates on home_locality_id. Non-group content
    // must have one. Group content may be deliberately unlocated (#72: a group with no location,
    // or an existing group event with none stored). A failed group lookup blocks saving, so an
    // event is never saved as unlocated by accident.
    if (homeLocalitySource === 'group-error') {
      newErrors.location = t('common:generic_error');
    } else if (!homeLocalityId && !isGroupContent) {
      newErrors.location = t('error_no_anchor');
    }

    if (!eventDetails.start_date) {
      newErrors.start_date = t('error_start_date_required');
    }

    // End must not precede start — wall-clock comparison via the Commit A helper
    if (eventDetails.start_date && eventDetails.end_date) {
      const start = parseEventDate(eventDetails.start_date);
      const end = parseEventDate(eventDetails.end_date);
      if (start && end && end < start) {
        newErrors.end_date = t('error_end_before_start');
      }
    }

    // Step 7B: Block submission if user has no home area
    if (homeLocalitySource === 'no-home') {
      newErrors.home_locality = t('no_home_prompt');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveAsDraft = async () => {
    addBreadcrumb('form-submit', { action: 'event-draft', component: 'AddEditEvent' });
    // Validate required fields for draft
    const draftErrors = {};
    if (!formData.title.trim()) draftErrors.title = t('error_title_required');
    if (!isLocalityMode && !formData.community_id) draftErrors.community_id = t('error_select_group');
    if (homeLocalitySource === 'group-error') draftErrors.location = t('common:generic_error');
    else if (!isGroupContent && !homeLocalityId) draftErrors.location = t('error_no_anchor');
    if (!eventDetails.start_date) draftErrors.start_date = t('error_start_date_required');
    if (homeLocalitySource === 'no-home') draftErrors.home_locality = t('articles:no_home_prompt');
    if (Object.keys(draftErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...draftErrors }));
      setModal({
        visible: true,
        message: t('error_fill_required_fields'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return;
    }

    setLoading(true);
    try {
      // Guard: refuse to write if the session token is null/stale
      const authToken = await supabaseFetch.getAuthToken();
      if (!authToken) {
        console.error('handleSaveAsDraft: no auth token — session expired, refusing anon insert');
        const err = new Error('SESSION_EXPIRED');
        err.code = 'SESSION_EXPIRED';
        throw err;
      }

      let coverImageUrl = formData.cover_image_url;
      coverImageUrl = resolveCoverImageUrl(coverImageUrl);

      // LOCATION ASSIGNMENT: Get centroid from boundary_ids
      // Source: group's boundary_ids (group mode) or selectedArea.boundary_ids (locality mode)
      let locationLat = null;
      let locationLng = null;

      if (isGroupContent) {
        // Group content (including a group chosen in locality mode): the centroid of the boundary
        // it is placed in; no point when the group has no location (#72 follow-ups Parts 2, 4).
        // No legacy community_localities fallback.
        const point = await groupService.getBoundaryCentroid(homeLocalityId);
        locationLat = point?.lat ?? null;
        locationLng = point?.lng ?? null;
      } else if (isLocalityMode) {
        // A locality event: the centroid of the parish it is stored in (home_locality_id — the
        // member's own parish, or the parish they chose), not the smallest parish of their whole
        // area (docs/briefs/2026-09-29-own-parish-everywhere.md). The same rule as group content.
        const point = await groupService.getBoundaryCentroid(homeLocalityId);
        locationLat = point?.lat ?? null;
        locationLng = point?.lng ?? null;
      }
      console.log('Location for event (draft):', { locationLat, locationLng, isLocalityMode, community_id: formData.community_id });

      const articleData = {
        title: formData.title,
        summary: formData.summary,
        content: formData.content,
        article_type: 'event',
        cover_image_url: coverImageUrl,
        image_caption: formData.image_caption || null,
        // A group chosen in locality mode makes it group content (#72 follow-ups Part 2).
        community_id: formData.community_id && formData.community_id.trim() !== '' ? formData.community_id : null,
        author_id: userId,
        is_public: false, // Drafts are not public
        is_published: false,
        updated_at: new Date().toISOString(),
        tag_slugs: tagSlugs.length > 0 ? tagSlugs : null,
        // Add location_point if we have coordinates
        // Group content with no point writes an explicit null, so an edit clears a stale point.
        ...(locationLat && locationLng ? {
          location_point: `SRID=4326;POINT(${locationLng} ${locationLat})`
        } : isGroupContent ? { location_point: null } : {}),
        // Step 7B: home_locality_id tagging
        home_locality_id: homeLocalityId || null
      };

      let articleResult, articleError;
      let articleId;

      const existingDraftId = (mode === 'edit' && event?.id) ? event.id : savedArticleId;
      await keepStoredFields(articleData, existingDraftId);
      if (existingDraftId) {
        ({ data: articleResult, error: articleError } = await supabaseFetch.update(
          'articles',
          articleData,
          { eq: { id: existingDraftId }, authToken }
        ));
        articleId = existingDraftId;
      } else {
        ({ data: articleResult, error: articleError } = await supabaseFetch.insert(
          'articles',
          articleData,
          { authToken }
        ));
        articleId = articleResult?.id;
        setSavedArticleId(articleId);
      }

      if (articleError) throw articleError;

      // Save event details
      const eventDetailsData = {
        article_id: articleId,
        start_date: eventDetails.start_date || null,
        end_date: eventDetails.end_date || null,
        display_from: eventDetails.display_from || null,
        is_all_day: eventDetails.is_all_day,
        timezone: eventDetails.timezone || 'UTC',
        venue_name: eventDetails.venue_name || null,
        venue_address: eventDetails.venue_address || null,
        venue_postcode: eventDetails.venue_postcode || null,
        is_online: eventDetails.is_online,
        max_attendees: eventDetails.max_attendees ? parseInt(eventDetails.max_attendees) : null,
        price_info: eventDetails.price_info || null,
        contact_email: eventDetails.contact_email || null,
        contact_phone: eventDetails.contact_phone || null,
        requires_rsvp: eventDetails.requires_rsvp,
        rsvp_deadline: eventDetails.rsvp_deadline || null
      };

      // Check if event_details record exists
      const { data: existingEventDetails } = await supabaseFetch.query(
        'event_details',
        { select: 'article_id', eq: { article_id: articleId }, single: true }
      );

      if (existingEventDetails) {
        // Update existing event details
        const { error: eventDetailsError } = await supabaseFetch.update(
          'event_details',
          eventDetailsData,
          { eq: { article_id: articleId }, authToken }
        );

        if (eventDetailsError) throw eventDetailsError;
      } else {
        // Insert new event details
        const { error: eventDetailsError } = await supabaseFetch.insert(
          'event_details',
          eventDetailsData,
          { authToken }
        );

        if (eventDetailsError) throw eventDetailsError;
      }

      // Notify that articles have changed so the feed can reload
      window.dispatchEvent(new CustomEvent('ournet:articlesChanged'));

      // Show modal instead of alert; navigation will happen when user confirms
      markSaved();
      setModal({
        visible: true,
        message: t('success_saved_draft'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          if (mode === 'edit' && onBack) {
            onBack();
          } else {
            window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'articles', scrollToTop: true } }));
          }
        }
      });
    } catch (error) {
      console.error('Error saving draft:', error);
      addCaughtError(error.message || String(error), 'AddEditEvent:saveDraft');
      const isSessionExpired = error?.code === 'SESSION_EXPIRED' || error?.message === 'SESSION_EXPIRED';
      const draftErrorMessage = isSessionExpired
        ? t('events:session_expired')
        : isGroupClosingError(error)
        ? t(GROUP_CLOSING_MESSAGE_KEY)
        : isRateLimited(error)
        ? rateLimitMessage(error, t)
        : t('error_failed_to_save_draft');
      setModal({
        visible: true,
        title: isRateLimited(error) ? t(RATE_LIMIT_TITLE_KEY) : undefined,
        message: draftErrorMessage,
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          if (isSessionExpired) {
            window.dispatchEvent(new CustomEvent('ournet:showAuthRequired', {
              detail: { message: draftErrorMessage }
            }));
          }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePostEvent = async () => {
    addBreadcrumb('form-submit', { action: 'event-publish', component: 'AddEditEvent' });
    if (!validateForm()) {
      setModal({
        visible: true,
        message: t('error_fill_required_fields'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
      return;
    }

    setLoading(true);
    try {
      // Guard: refuse to write if the session token is null/stale
      const authToken = await supabaseFetch.getAuthToken();
      if (!authToken) {
        console.error('handlePostEvent: no auth token — session expired, refusing anon insert');
        const err = new Error('SESSION_EXPIRED');
        err.code = 'SESSION_EXPIRED';
        throw err;
      }

      let coverImageUrl = formData.cover_image_url;
      coverImageUrl = resolveCoverImageUrl(coverImageUrl);

      // LOCATION ASSIGNMENT: Get centroid from boundary_ids
      // Source: group's boundary_ids (group mode) or selectedArea.boundary_ids (locality mode)
      let locationLat = null;
      let locationLng = null;

      if (isGroupContent) {
        // Group content (including a group chosen in locality mode): the centroid of the boundary
        // it is placed in; no point when the group has no location (#72 follow-ups Parts 2, 4).
        // No legacy community_localities fallback.
        const point = await groupService.getBoundaryCentroid(homeLocalityId);
        locationLat = point?.lat ?? null;
        locationLng = point?.lng ?? null;
      } else if (isLocalityMode) {
        // A locality event: the centroid of the parish it is stored in (home_locality_id — the
        // member's own parish, or the parish they chose), not the smallest parish of their whole
        // area (docs/briefs/2026-09-29-own-parish-everywhere.md). The same rule as group content.
        const point = await groupService.getBoundaryCentroid(homeLocalityId);
        locationLat = point?.lat ?? null;
        locationLng = point?.lng ?? null;
      }
      console.log('Location for event (publish):', { locationLat, locationLng, isLocalityMode, community_id: formData.community_id });

      const articleData = {
        title: formData.title,
        summary: formData.summary,
        content: formData.content,
        article_type: 'event',
        cover_image_url: coverImageUrl,
        image_caption: formData.image_caption || null,
        // A group chosen in locality mode makes it group content (#72 follow-ups Part 2).
        community_id: formData.community_id && formData.community_id.trim() !== '' ? formData.community_id : null,
        author_id: userId,
        is_public: formData.is_public,
        is_published: true,
        updated_at: new Date().toISOString(),
        tag_slugs: tagSlugs.length > 0 ? tagSlugs : null,
        // Add location_point if we have coordinates
        // Group content with no point writes an explicit null, so an edit clears a stale point.
        ...(locationLat && locationLng ? {
          location_point: `SRID=4326;POINT(${locationLng} ${locationLat})`
        } : isGroupContent ? { location_point: null } : {}),
        // Step 7B: home_locality_id tagging
        home_locality_id: homeLocalityId || null
      };

      let articleResult, articleError;
      let articleId;

      const existingPublishId = (mode === 'edit' && event?.id) ? event.id : savedArticleId;
      await keepStoredFields(articleData, existingPublishId);
      if (existingPublishId) {
        ({ data: articleResult, error: articleError } = await supabaseFetch.update(
          'articles',
          articleData,
          { eq: { id: existingPublishId }, authToken }
        ));
        articleId = existingPublishId;
      } else {
        ({ data: articleResult, error: articleError } = await supabaseFetch.insert(
          'articles',
          articleData,
          { authToken }
        ));
        articleId = articleResult?.id;
        setSavedArticleId(articleId);
      }

      if (articleError) throw articleError;

      // Save event details
      const eventDetailsData = {
        article_id: articleId,
        start_date: eventDetails.start_date || null,
        end_date: eventDetails.end_date || null,
        display_from: eventDetails.display_from || null,
        is_all_day: eventDetails.is_all_day,
        timezone: eventDetails.timezone || 'UTC',
        venue_name: eventDetails.venue_name || null,
        venue_address: eventDetails.venue_address || null,
        venue_postcode: eventDetails.venue_postcode || null,
        is_online: eventDetails.is_online,
        max_attendees: eventDetails.max_attendees ? parseInt(eventDetails.max_attendees) : null,
        price_info: eventDetails.price_info || null,
        contact_email: eventDetails.contact_email || null,
        contact_phone: eventDetails.contact_phone || null,
        requires_rsvp: eventDetails.requires_rsvp,
        rsvp_deadline: eventDetails.rsvp_deadline || null
      };

      // Check if event_details record exists
      const { data: existingEventDetails } = await supabaseFetch.query(
        'event_details',
        { select: 'article_id', eq: { article_id: articleId }, single: true }
      );

      if (existingEventDetails) {
        // Update existing event details
        const { error: eventDetailsError } = await supabaseFetch.update(
          'event_details',
          eventDetailsData,
          { eq: { article_id: articleId }, authToken }
        );

        if (eventDetailsError) throw eventDetailsError;
      } else {
        // Insert new event details
        const { error: eventDetailsError } = await supabaseFetch.insert(
          'event_details',
          eventDetailsData,
          { authToken }
        );

        if (eventDetailsError) throw eventDetailsError;
      }

      // Notify that articles have changed so the feed can reload
      window.dispatchEvent(new CustomEvent('ournet:articlesChanged'));

      // Show modal instead of alert; navigation will happen when user confirms
      markSaved();
      setModal({
        visible: true,
        message: mode === 'edit' ? t('success_event_updated') : t('success_event_posted'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          if (mode === 'edit' && onBack) {
            onBack();
          } else {
            // Return to the front page of the context the event was created in.
            // 'feed' preserves currentContext + selectedGroup, so a group event lands
            // on that group's front page and a locality event on the locality feed.
            window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'feed', scrollToTop: true } }));
          }
        }
      });
    } catch (error) {
      console.error('Error posting event:', error);
      console.error('Full error details:', JSON.stringify(error, null, 2));
      addCaughtError(error.message || String(error), 'AddEditEvent:publish');
      const isSessionExpired = error?.code === 'SESSION_EXPIRED' || error?.message === 'SESSION_EXPIRED';
      const publishErrorMessage = isSessionExpired
        ? t('events:session_expired')
        : isGroupClosingError(error)
        ? t(GROUP_CLOSING_MESSAGE_KEY)
        : isRateLimited(error)
        ? rateLimitMessage(error, t)
        : t('error_failed_to_post', { error: error.message || error.toString() });
      setModal({
        visible: true,
        title: isRateLimited(error) ? t(RATE_LIMIT_TITLE_KEY) : undefined,
        message: publishErrorMessage,
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          if (isSessionExpired) {
            window.dispatchEvent(new CustomEvent('ournet:showAuthRequired', {
              detail: { message: publishErrorMessage }
            }));
          }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Cancel asks only when something changed (#78), then leaves the way the form was opened.
  const handleCancel = async () => {
    if (await confirmLeave()) leaveForm();
  };


  return (
    <div className="min-h-screen bg-[#EDF2F8] flex flex-col">
      {/* Header */}
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={currentContext}
        onBack={onBack}
        headerTitle={headerTitle}
        onCheckUnsaved={confirmLeave}
      />

      {/* Main Content */}
      <div className="flex-1 pt-[100px]" style={{ paddingBottom: isMobile ? '120px' : '220px' }}>
        <div className="max-w-[1600px]" style={{ marginTop: '0px', marginLeft: isMobile ? '16px' : '40px', marginRight: isMobile ? '16px' : '0px' }}>
          <div className={isMobile ? 'flex flex-col gap-6' : 'flex gap-8'}>
            {/* Left Column - Fixed 480px on desktop, full width on mobile - shows SECOND on mobile */}
            <div className="space-y-3" style={isMobile ? { width: '100%', order: 2 } : { width: '480px', flexShrink: 0 }}>
              {/* Hero Image */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('field_hero_image')}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-white" style={isMobile ? { width: '100%', height: '280px' } : { width: '448px', height: '336px' }}>
                  {heroImageDisplay ? (
                    <div className="relative h-full">
                      <img
                        src={heroImageDisplay}
                        alt="Hero preview"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        onClick={() => setShowHeroCropper(true)}
                        className="absolute top-2 right-2 bg-[var(--theme-primary)] text-white px-3 py-1 rounded-lg text-sm hover:bg-[#023872]"
                      >
                        {t('button_change')}
                      </button>
                      <button
                        onClick={() => {
                          setHeroImageDisplay('');
                          setHeroImageThumbnail('');
                          setImagePreview(null);
                          setFormData(prev => ({ ...prev, cover_image_url: '' }));
                        }}
                        className="absolute top-2 right-20 bg-red-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-red-600"
                      >
                        {t('button_remove')}
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Upload className="mb-2 text-[var(--theme-primary)]" size={48} />
                      <p className="text-[var(--theme-primary)] font-semibold mb-2 text-sm">
                        {t('upload_select_hero_image')}<br />{t('upload_from_device')}
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowHeroCropper(true)}
                        className="inline-block px-6 py-2 bg-[var(--theme-primary)] text-white rounded-lg cursor-pointer hover:bg-[#023872] transition-colors"
                      >
                        {t('button_choose_image')}
                      </button>
                      <p className="text-xs text-gray-600 mt-2">
                        {t('image_aspect_hint')}
                      </p>
                    </div>
                  )}
                  {errors.image && (
                    <p className="text-red-500 text-sm mt-2">{errors.image}</p>
                  )}
                </div>

                {/* Image Caption */}
                {imagePreview && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('field_photo_credit')}
                    </label>
                    <input
                      type="text"
                      value={formData.image_caption}
                      onChange={(e) => handleInputChange('image_caption', e.target.value)}
                      placeholder={t('placeholder_photo_credit')}
                      maxLength={ARTICLE_IMAGE_CAPTION_MAX}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)] text-sm"
                    />
                    <p className="text-gray-500 text-xs mt-1">
                      {t('char_count', { current: formData.image_caption.length, max: ARTICLE_IMAGE_CAPTION_MAX })}
                    </p>
                  </div>
                )}
              </div>

            </div>

            {/* Middle Column - Flexible - shows FIRST on mobile */}
            <div className="space-y-3 flex-1" style={isMobile ? { order: 1 } : {}}>
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('field_title')}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder={t('placeholder_title')}
                  maxLength={ARTICLE_TITLE_MAX}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:border-[var(--theme-primary)]`}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  {t('char_count', { current: formData.title.length, max: ARTICLE_TITLE_MAX })}
                </p>
              </div>

              {/* Introduction (was Summary) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('field_introduction')}
                </label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => handleInputChange('summary', e.target.value)}
                  placeholder={t('placeholder_introduction')}
                  maxLength={ARTICLE_SUMMARY_MAX}
                  rows={3}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.summary ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:border-[var(--theme-primary)] resize-none`}
                />
                {errors.summary && (
                  <p className="text-red-500 text-sm mt-1">{errors.summary}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  {t('char_count', { current: formData.summary.length, max: ARTICLE_SUMMARY_MAX })}
                </p>
              </div>

              {/* Event Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('field_event_description')}
                </label>
                {/* The shared formatting toolbar (group form addendum 3 item 2): the same markers
                    the event page renders through formatTextWithStyles. */}
                <div style={{ marginBottom: '8px' }}>
                  <FormattingToolbar onFormat={applyContentFormatting} className="inline-flex items-center gap-1 border border-gray-300 rounded-lg p-1" />
                </div>
                <textarea
                  ref={contentTextareaRef}
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder={t('placeholder_event_description')}
                  maxLength={ARTICLE_CONTENT_MAX}
                  rows={12}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.content ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:border-[var(--theme-primary)] resize-none`}
                />
                {errors.content && (
                  <p className="text-red-500 text-sm mt-1">{errors.content}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  {t('char_count', { current: formData.content.length, max: ARTICLE_CONTENT_MAX })}
                </p>
              </div>

              {/* Tags — closed-vocabulary picker (replaces the old free-text
                  article_tags control; brief docs/briefs/2026-08-11-tag-picker-
                  addeditevent-build.md §2/§3). No free entry: the steward ticks
                  ratified terms grouped under their parent category, mirroring
                  PendingEvents.jsx's grouped-checkbox idiom (the house shape for
                  this repo — no dedicated MultiSelect component exists). Selected
                  terms also render as removable chips, same visual as the control
                  being replaced, so the form stays familiar. */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('field_tags')}
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  {t('tags_picker_instructions')}
                </p>

                {tagVocabularyLoading ? null : tagVocabulary.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('tags_none_available')}</p>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-300 p-4 space-y-4">
                    {tagVocabulary.map(category => (
                      <div key={category.id}>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          {tTags(category.i18n_key)}
                        </h4>
                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                          {category.terms.map(term => (
                            <label key={term.id} className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={tagSlugs.includes(term.id)}
                                onChange={() => handleToggleTagSlug(term.id)}
                              />
                              {tTags(term.i18n_key)}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Selected tags as removable chips — same visual as the free-text
                    control this replaces (teal pill + ×), just now driven by ticks
                    rather than typed text. */}
                {tagSlugs.length > 0 && (
                  <div className="bg-white rounded-lg p-4 min-h-[60px] border border-gray-300 mt-4">
                    <div className="flex flex-wrap gap-2">
                      {tagSlugs.map(slug => {
                        const term = tagVocabulary.flatMap(c => c.terms).find(term => term.id === slug);
                        return (
                          <button
                            key={slug}
                            type="button"
                            onClick={() => handleToggleTagSlug(slug)}
                            className="bg-teal-500 text-white px-4 py-2 rounded-full flex items-center gap-2 hover:bg-teal-600 transition-colors text-sm font-medium"
                          >
                            <span>{term ? tTags(term.i18n_key) : slug}</span>
                            <span className="text-lg leading-none">×</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Submit to Group (optional in locality mode) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('field_submit_to_group')}
                </label>
                <select
                  value={formData.community_id}
                  onChange={(e) => handleInputChange('community_id', e.target.value)}
                  disabled={isPlatformContent}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    errors.community_id ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:border-[var(--theme-primary)]`}
                >
                  {isLocalityMode ? (
                    <option value="">{t('option_locality_no_group')}</option>
                  ) : (
                    <option value="">{t('option_select')}</option>
                  )}
                  {savedGroupUnavailable && (
                    <option value={savedCommunityId} disabled>{t('common:saved_group_unavailable_option')}</option>
                  )}
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                {savedGroupUnavailable && (
                  <p role="status" className="text-sm mt-1" style={{ color: '#92400E' }}>{t('common:saved_group_unavailable')}</p>
                )}
                {isPlatformContent && (
                  <p role="status" className="text-sm mt-1" style={{ color: tokens.colors.textSecondary }}>{t('common:platform_content_notice')} {t('common:platform_content_group_locked')}</p>
                )}
                {isLocalityMode && (
                  <p className="text-gray-500 text-sm mt-1">{t('group_optional_hint')}</p>
                )}
                {/* #72: where the event will appear. Public group: one boundary → a read-only
                    line; several → the picker below, limited to the group's boundaries (interim,
                    until #73). Private group: the members-only line. No location →
                    the notice. */}
                {!homeLocalityLoading && isGroupContent && homeLocalitySource === 'group-error' && !errors.location && (
                  <p className="text-red-500 text-sm mt-1">{t('common:generic_error')}</p>
                )}
                {/* A private group's content is for its members only — that line takes precedence
                    over the unlocated notice, whose "show its content locally" does not apply. */}
                {!homeLocalityLoading && isGroupContent && isPrivateGroupContent && homeLocalitySource !== 'group-error' && (
                  <p className="text-gray-500 text-sm mt-1">{t('articles:group_content_members_only')}</p>
                )}
                {!homeLocalityLoading && isGroupContent && !isPrivateGroupContent && groupLocalityStatus === 'unlocated' && !homeLocalityId && (
                  <p className="text-gray-500 text-sm mt-1">{t('group_unlocated_notice')}</p>
                )}
                {!homeLocalityLoading && isGroupContent && !isPrivateGroupContent && groupLocalityStatus === 'located' && homeLocalityBoundaries.length <= 1 && homeLocalityId && homeLocalityName && (
                  <p className="text-gray-500 text-sm mt-1">{t('articles:group_content_appears_in', { locality: homeLocalityName })}</p>
                )}
                {errors.community_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.community_id}</p>
                )}
                {errors.location && (
                  <p className="text-red-500 text-sm mt-1">{errors.location}</p>
                )}
              </div>

              {/* Own-parish addendum 2: a locality event with no group chooses its parish as the
                  article form does — from the browsed (else home) area's parishes, default its own. */}
              {isLocalityMode && !isGroupContent && homeLocalitySource === 'area-choice' && (
                <ParishSelect
                  label={t('articles:publishing_to')}
                  hint={t('locality_feed_info')}
                  options={homeLocalityBoundaries}
                  value={homeLocalityId}
                  onChange={handleLocalityPick}
                  disabled={isPlatformContent}
                  readOnlyName={homeLocalityName || selectedArea?.display_name || t('articles:your_home_locality')}
                />
              )}

              {/* Step 7B: Boundary picker (a public group's own boundaries) */}
              {!homeLocalityLoading && homeLocalityBoundaries.length > 1 && homeLocalitySource !== 'no-home' && homeLocalitySource !== 'area-choice' && !isPrivateGroupContent && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('articles:locality_picker_label')}
                  </label>
                  <select
                    value={homeLocalityId || ''}
                    onChange={(e) => handleLocalityPick(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  >
                    {!homeLocalityId && <option value="">{t('option_select')}</option>}
                    {homeLocalityBoundaries.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Step 7B: No-home soft block */}
              {!homeLocalityLoading && homeLocalitySource === 'no-home' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 mb-3">{t('articles:no_home_prompt')}</p>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'locations' } }))}
                    className="px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg text-sm font-semibold hover:bg-[#023872] transition-colors"
                  >
                    {t('articles:no_home_button')}
                  </button>
                </div>
              )}

              {/* Event Details Section */}
              <div className="pt-6 border-t-2 border-gray-300">
                <h3 className="text-lg font-bold text-gray-700 mb-4">{t('section_event_details')}</h3>

                {/* Start Date */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_start_date_time')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={eventDetails.start_date}
                    onChange={(e) => handleEventDetailChange('start_date', e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.start_date ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:border-[var(--theme-primary)]`}
                  />
                  {errors.start_date && (
                    <p className="text-red-500 text-sm mt-1">{errors.start_date}</p>
                  )}
                </div>

                {/* End Date */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_end_date_time')}
                  </label>
                  <input
                    type="datetime-local"
                    value={eventDetails.end_date}
                    onChange={(e) => handleEventDetailChange('end_date', e.target.value)}
                    // Prevent picking an end before the start. Tracks the start state, so it
                    // updates reactively; undefined (not '') when start is empty so the field
                    // isn't locked before a start is chosen. validateForm stays the backstop.
                    min={eventDetails.start_date || undefined}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.end_date ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:border-[var(--theme-primary)]`}
                  />
                  {errors.end_date && (
                    <p className="text-red-500 text-sm mt-1">{errors.end_date}</p>
                  )}
                </div>

                {/* Show From — when the event starts appearing in the feed.
                    Blank = appears as soon as it's published (display_from stays NULL). */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_show_from')}
                  </label>
                  <input
                    type="datetime-local"
                    value={eventDetails.display_from}
                    onChange={(e) => handleEventDetailChange('display_from', e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  />
                  {/* datetime-local ignores the placeholder attribute, so the "Optional"
                      hint lives here as greyed helper text. */}
                  <p className="text-gray-400 text-sm mt-1">{t('placeholder_optional')}</p>
                </div>

                {/* All Day Event Checkbox */}
                <div className="mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={eventDetails.is_all_day}
                      onChange={(e) => handleEventDetailChange('is_all_day', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm font-semibold text-gray-700">{t('field_all_day_event')}</span>
                  </label>
                </div>

                {/* Timezone */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_timezone')}
                  </label>
                  <input
                    type="text"
                    value={eventDetails.timezone}
                    onChange={(e) => handleEventDetailChange('timezone', e.target.value)}
                    placeholder={t('placeholder_timezone')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  />
                </div>

                {/* Venue Name */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_venue_name')}
                  </label>
                  <input
                    type="text"
                    value={eventDetails.venue_name}
                    onChange={(e) => handleEventDetailChange('venue_name', e.target.value)}
                    placeholder={t('placeholder_venue_name')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  />
                </div>

                {/* Venue Address */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_venue_address')}
                  </label>
                  <input
                    type="text"
                    value={eventDetails.venue_address}
                    onChange={(e) => handleEventDetailChange('venue_address', e.target.value)}
                    placeholder={t('placeholder_venue_address')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  />
                </div>

                {/* Venue Postcode */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_venue_postcode')}
                  </label>
                  <input
                    type="text"
                    value={eventDetails.venue_postcode}
                    onChange={(e) => handleEventDetailChange('venue_postcode', e.target.value)}
                    placeholder={t('placeholder_venue_postcode')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  />
                </div>

                {/* Online Event Checkbox */}
                <div className="mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={eventDetails.is_online}
                      onChange={(e) => handleEventDetailChange('is_online', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm font-semibold text-gray-700">{t('field_online_event')}</span>
                  </label>
                </div>

                {/* Max Attendees */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_max_attendees')}
                  </label>
                  <input
                    type="number"
                    value={eventDetails.max_attendees}
                    onChange={(e) => handleEventDetailChange('max_attendees', e.target.value)}
                    placeholder={t('placeholder_max_attendees')}
                    min="0"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  />
                </div>

                {/* Price Info */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_price_information')}
                  </label>
                  <input
                    type="text"
                    value={eventDetails.price_info}
                    onChange={(e) => handleEventDetailChange('price_info', e.target.value)}
                    placeholder={t('placeholder_price_info')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  />
                </div>

                {/* Contact Email */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_contact_email')}
                  </label>
                  <input
                    type="email"
                    value={eventDetails.contact_email}
                    onChange={(e) => handleEventDetailChange('contact_email', e.target.value)}
                    placeholder={t('placeholder_contact_email')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  />
                </div>

                {/* Contact Phone */}
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('field_contact_phone')}
                  </label>
                  <input
                    type="tel"
                    value={eventDetails.contact_phone}
                    onChange={(e) => handleEventDetailChange('contact_phone', e.target.value)}
                    placeholder={t('placeholder_contact_phone')}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                  />
                </div>

                {/* Requires RSVP Checkbox */}
                <div className="mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={eventDetails.requires_rsvp}
                      onChange={(e) => handleEventDetailChange('requires_rsvp', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm font-semibold text-gray-700">{t('field_requires_rsvp')}</span>
                  </label>
                </div>

                {/* RSVP Deadline */}
                {eventDetails.requires_rsvp && (
                  <div className="mb-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('field_rsvp_deadline')}
                    </label>
                    <input
                      type="datetime-local"
                      value={eventDetails.rsvp_deadline}
                      onChange={(e) => handleEventDetailChange('rsvp_deadline', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-[var(--theme-primary)]"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Fixed 300px (Reserved for future Video/Event features) - Hidden on mobile */}
            {!isMobile && (
              <div className="space-y-6" style={{ width: '300px', flexShrink: 0 }}>
                <div className="bg-white rounded-lg p-4 border border-gray-300">
                  <p className="text-gray-400 text-sm text-center">
                    {t('reserved_future_features')}
                  </p>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Footer */}
      <FooterContainer viewType="form" isMobile={isMobile}>
        <FooterButtonsForm
          onSaveDraft={handleSaveAsDraft}
          onCancel={handleCancel}
          onCreateAndExit={handlePostEvent}
          isEditMode={mode === 'edit'}
        />
      </FooterContainer>

      {leaveDialog}

      <ConfirmationModal
        visible={modal.visible}
        title={modal.title || t('modal_confirmation')}
        message={modal.message}
        okLabel={modal.okLabel}
        cancelLabel={modal.cancelLabel}
        onOk={() => {
          try { modal.onOk && modal.onOk(); } catch (e) { console.error(e); setModal({ visible: false, message: '', onOk: null }); }
        }}
        onCancel={() => {
          try {
            if (modal.onCancel) modal.onCancel();
            else setModal({ visible: false, message: '', onOk: null });
          } catch (e) {
            console.error(e);
            setModal({ visible: false, message: '', onOk: null });
          }
        }}
      />

      {/* Hero Image Cropper Modal */}
      <ImageCropper
        isOpen={showHeroCropper}
        onClose={() => setShowHeroCropper(false)}
        onSave={handleHeroImageSave}
        aspectMode="hero"
        bucket="hero-images"
        userId={userId}
      />
    </div>
  );
};

export default AddEditEvent;
